import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { saveAuth, isAuthed, getUser } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { absUrl } from "@/components/PhotoUploader";
import { Briefcase, ShieldCheck, MapPin, ArrowUpRight, Building2, CalendarDays, BedDouble, ArrowRight, Package, X, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

function AuthDialog({ mode = "login", onDone, trigger }) {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [m, setM] = useState(mode);
  const [f, setF] = useState({ email: "", password: "", company_name: "", vat_number: "", country: "IT", phone: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const path = m === "login" ? "/auth/login" : "/auth/register-trade";
      const body = m === "login" ? { email: f.email, password: f.password } : f;
      const r = await api.post(path, body);
      saveAuth(r.data.token, r.data.user);
      toast.success("OK");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{m === "login" ? t("dash.trade_login") : t("dash.trade_register")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="trade-auth-form">
          {m === "register" && (
            <>
              <div><Label>{t("dash.trade_company")}</Label><Input required value={f.company_name} onChange={(e)=>setF({...f, company_name:e.target.value})} data-testid="trade-company"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("dash.trade_vat")}</Label><Input required value={f.vat_number} onChange={(e)=>setF({...f, vat_number:e.target.value})} data-testid="trade-vat"/></div>
                <div><Label>{t("dash.trade_country")}</Label><Input value={f.country} onChange={(e)=>setF({...f, country:e.target.value})}/></div>
              </div>
            </>
          )}
          <div><Label>Email</Label><Input type="email" required value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} data-testid="trade-email"/></div>
          <div><Label>{t("auth.password")}</Label><Input type="password" required minLength={6} value={f.password} onChange={(e)=>setF({...f, password:e.target.value})} data-testid="trade-password"/></div>
          <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="trade-auth-submit">
            {m === "login" ? t("auth.submit_login") : t("auth.submit_register")}
          </Button>
          <button type="button" onClick={() => setM(m === "login" ? "register" : "login")} className="text-xs text-muted-foreground hover:text-primary w-full text-center pt-2">
            {m === "login" ? (lang === "it" ? "Nuova agenzia? Registrati" : "New agency? Register") : (lang === "it" ? "Hai già un account? Accedi" : "Have an account? Sign in")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OfferCard({ o, onBuy, onAddToBundle, isTrade, format, inBundle }) {
  const { t, lang } = useLang();
  const remaining = o.rooms_available - (o.rooms_sold || 0);
  const nights = Math.max(1, Math.round((new Date(o.check_out) - new Date(o.check_in)) / 86400000));
  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden group lift" data-testid={`trade-offer-${o.id}`}>
      <div className="aspect-[16/9] overflow-hidden bg-muted relative">
        {o.hero_image && <img src={absUrl(o.hero_image)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
        {o.verified && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] bg-sage text-white rounded-full px-2 py-1">
            <ShieldCheck className="w-3 h-3"/> {lang === "it" ? "Verificata" : "Verified"}
          </span>
        )}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] bg-ink/80 text-sand rounded-full px-2 py-1">
          <MapPin className="w-3 h-3"/> {o.city}
        </span>
      </div>
      <div className="p-5">
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage mb-1">{o.property_name}</div>
        <h3 className="font-display text-2xl mb-2">{o.title}</h3>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{lang === "it" ? o.description_it : o.description_en}</p>
        <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground border-t border-border pt-3 mb-4">
          <div><CalendarDays className="w-3.5 h-3.5 inline mr-1 text-sage"/>{o.check_in.slice(5)} → {o.check_out.slice(5)}</div>
          <div><BedDouble className="w-3.5 h-3.5 inline mr-1 text-sage"/>{remaining}/{o.rooms_available} {t("dash.available")}</div>
          <div>≥ {o.min_nights} {lang === "it" ? "notti" : "nights"}</div>
        </div>
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="font-display text-3xl text-primary">{format(o.price_per_room_night, { digits: 0 })}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{lang === "it" ? "camera / notte" : "room / night"}</div>
          </div>
          <div className="flex flex-col gap-2">
            {isTrade && (
              <Button size="sm" variant="outline" onClick={()=>onAddToBundle(o)} disabled={inBundle} className="lift" data-testid={`trade-bundle-${o.id}`}>
                <Package className="w-3.5 h-3.5 mr-1"/>{inBundle ? (lang==="it"?"Aggiunta":"Added") : (lang==="it"?"Al pacchetto":"To bundle")}
              </Button>
            )}
            <Button size="sm" onClick={()=>onBuy(o)} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid={`trade-buy-${o.id}`}>
              {isTrade ? t("dash.trade_buy") : t("dash.trade_login")} <ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BundlePanel({ items, offers, setItems, format, onCheckout }) {
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(lang === "it" ? "Pacchetto tour" : "Tour package");
  const details = items.map((it) => ({ ...it, offer: offers.find((o) => o.id === it.offer_id) })).filter(x => x.offer);
  const nightsOf = (o) => Math.max(1, Math.round((new Date(o.check_out) - new Date(o.check_in)) / 86400000));
  const totalFor = (d) => d.rooms * nightsOf(d.offer) * d.offer.price_per_room_night;
  const total = details.reduce((s, d) => s + totalFor(d), 0);
  const rm = (id) => setItems(items.filter((i) => i.offer_id !== id));
  const setRooms = (id, r) => setItems(items.map((i) => i.offer_id === id ? { ...i, rooms: r } : i));
  const checkout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trade/bundle/checkout", { name, items, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); setBusy(false); }
  };
  const previewPdf = async () => {
    try {
      const r = await api.post("/trade/bundle/preview-pdf", { name, items }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  if (!items.length) return null;
  return (
    <div className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[400px] max-h-[75vh] bg-card border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden" data-testid="bundle-panel">
      <div className="bg-ink text-sand p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-primary"/>
          <span className="font-display text-xl">{lang === "it" ? "Pacchetto tour" : "Tour bundle"}</span>
          <span className="text-[10px] bg-primary text-primary-foreground rounded-full px-2 py-0.5">{items.length}</span>
        </div>
        <button onClick={() => setItems([])} className="text-sand/70 hover:text-sand" data-testid="bundle-clear"><X className="w-4 h-4"/></button>
      </div>
      <div className="p-3">
        <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder={lang==="it"?"Nome del pacchetto":"Bundle name"} className="text-sm" data-testid="bundle-name"/>
      </div>
      <div className="flex-1 overflow-y-auto px-3 space-y-3">
        {details.length > 1 && (
          <div className="border border-border rounded-md p-3 bg-sand" data-testid="bundle-timeline">
            <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-3">
              {lang === "it" ? "Il tuo itinerario" : "Your itinerary"}
            </div>
            <div className="relative">
              <div className="absolute top-3 left-2 right-2 h-0.5 bg-primary/30"/>
              <div className="relative flex justify-between items-start">
                {[...details].sort((a,b)=>a.offer.check_in.localeCompare(b.offer.check_in)).map((d, i) => (
                  <div key={d.offer_id} className="flex flex-col items-center text-center flex-1 min-w-0 px-1">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-[10px] font-bold z-10 shadow">
                      {i+1}
                    </div>
                    <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-ink truncate w-full" title={d.offer.city}>{d.offer.city}</div>
                    <div className="text-[9px] text-muted-foreground">{d.offer.check_in.slice(5)}</div>
                    <div className="text-[10px] text-primary font-semibold mt-1">{d.rooms}×{nightsOf(d.offer)}n</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {details.map((d) => {
          const remaining = d.offer.rooms_available - (d.offer.rooms_sold || 0);
          return (
            <div key={d.offer_id} className="border border-border rounded-md p-3 text-sm" data-testid={`bundle-item-${d.offer_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.offer.property_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.offer.title}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{d.offer.check_in} → {d.offer.check_out}</div>
                </div>
                <button onClick={()=>rm(d.offer_id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
              <div className="flex items-center justify-between gap-2">
                <Input type="number" min="1" max={remaining} value={d.rooms} onChange={(e)=>setRooms(d.offer_id, Math.max(1, Math.min(remaining, parseInt(e.target.value)||1)))} className="w-20 h-8 text-xs" data-testid={`bundle-rooms-${d.offer_id}`}/>
                <div className="font-display text-lg text-primary">{format(totalFor(d), { digits: 0 })}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-border p-3 space-y-2 bg-sand">
        <div className="flex justify-between text-xs text-muted-foreground"><span>{lang==="it"?"Fee 5%":"Fee 5%"}</span><span>{format(total * 0.05)}</span></div>
        <div className="flex justify-between items-end">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("dash.total")}</span>
          <span className="font-display text-2xl text-primary" data-testid="bundle-total">{format(total)}</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={previewPdf} variant="outline" className="flex-1 lift" data-testid="bundle-pdf-btn">
            <FileText className="w-4 h-4 mr-1"/>{lang === "it" ? "Preventivo PDF" : "PDF quote"}
          </Button>
          <Button onClick={checkout} disabled={busy || items.length === 0} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="bundle-checkout-btn">
            {busy ? t("vetrina.processing") : (lang === "it" ? "Checkout" : "Checkout")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BuyDialog({ offer, onClose, format }) {
  const { t, lang } = useLang();
  const [rooms, setRooms] = useState(1);
  const [busy, setBusy] = useState(false);
  const nights = Math.max(1, Math.round((new Date(offer.check_out) - new Date(offer.check_in)) / 86400000));
  const total = rooms * nights * offer.price_per_room_night;
  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trade/orders/checkout", { offer_id: offer.id, rooms, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); setBusy(false); }
  };
  const remaining = offer.rooms_available - (offer.rooms_sold || 0);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-3xl">{offer.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">{offer.property_name} · {offer.city} · {offer.check_in} → {offer.check_out} ({nights} {lang==="it"?"notti":"nights"})</div>
          <div>
            <Label>{t("dash.trade_rooms_available")} (max {remaining})</Label>
            <Input type="number" min="1" max={remaining} value={rooms} onChange={(e)=>setRooms(Math.max(1, Math.min(remaining, parseInt(e.target.value)||1)))} data-testid="trade-rooms-input"/>
          </div>
          <div className="bg-sand border border-border rounded-md p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>{rooms} × {nights} × {format(offer.price_per_room_night)}</span><b>{format(total)}</b></div>
            <div className="flex justify-between text-xs text-muted-foreground"><span>{lang==="it"?"Fee piattaforma 5%":"Platform fee 5%"}</span><span>{format(total * 0.05)}</span></div>
            <div className="border-t border-border/60 pt-2 flex justify-between text-lg font-display text-primary"><span>{t("dash.total")}</span><span data-testid="trade-total">{format(total)}</span></div>
          </div>
          <Button onClick={buy} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="trade-checkout-btn">
            {busy ? t("vetrina.processing") : t("dash.trade_buy")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Trade() {
  const { t, lang } = useLang();
  const { format } = useCurrency();
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [buying, setBuying] = useState(null);
  const [bundleItems, setBundleItems] = useState([]);
  const nav = useNavigate();
  const user = getUser();
  const isTrade = isAuthed() && user?.role === "trade";

  const load = () => {
    api.get("/trade/offers").then((r)=>setOffers(r.data)).catch(()=>{});
    if (isTrade) {
      api.get("/trade/orders/me").then((r)=>setOrders(r.data)).catch(()=>{});
      api.get("/trade/bundles/me").then((r)=>setBundles(r.data)).catch(()=>{});
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [isTrade]);

  const tryBuy = (o) => { if (isTrade) setBuying(o); else toast.error(lang==="it"?"Accedi come agenzia":"Sign in as agency"); };
  const addToBundle = (o) => {
    if (bundleItems.some((i) => i.offer_id === o.id)) return;
    setBundleItems([...bundleItems, { offer_id: o.id, rooms: 1 }]);
    toast.success(lang === "it" ? `Aggiunto: ${o.property_name}` : `Added: ${o.property_name}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar/>
      <section className="relative grain-bg bg-ink text-sand py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="uppercase tracking-[0.2em] text-xs text-primary font-semibold mb-4 flex items-center gap-2">
            <Briefcase className="w-4 h-4"/> {t("dash.trade_nav")}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight max-w-3xl leading-[1]" data-testid="trade-hero">
            {t("dash.trade_title")}
          </h1>
          <p className="mt-6 text-lg text-sand/80 max-w-2xl">{t("dash.trade_sub")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {!isTrade ? (
              <>
                <AuthDialog mode="register" onDone={load} trigger={<Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="trade-register-btn"><Building2 className="w-4 h-4 mr-1"/>{t("dash.trade_register")}</Button>}/>
                <AuthDialog mode="login" onDone={load} trigger={<Button size="lg" variant="outline" className="lift bg-transparent text-sand border-sand/40 hover:bg-sand/10" data-testid="trade-login-btn">{t("dash.trade_login")}</Button>}/>
              </>
            ) : (
              <div className="inline-flex items-center gap-2 bg-sand/10 rounded-full px-4 py-2 text-sm border border-sand/20">
                <Building2 className="w-4 h-4"/> {user?.structure_name}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-10">
          <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-3">— {t("dash.trade_offers")}</div>
          <h2 className="font-display text-4xl">{lang === "it" ? "Blocchi di camere in vendita" : "Room blocks on sale"}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trade-offers-grid">
          {offers.map((o) => <OfferCard key={o.id} o={o} onBuy={tryBuy} onAddToBundle={addToBundle} isTrade={isTrade} format={format} inBundle={bundleItems.some((i) => i.offer_id === o.id)}/>)}
        </div>
        {offers.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-20 border border-dashed border-border rounded-lg">
            {lang === "it" ? "Nessuna offerta al momento" : "No offers at the moment"}
          </div>
        )}
      </section>

      {isTrade && bundles.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-8">
          <h2 className="font-display text-3xl mb-6">{lang === "it" ? "Pacchetti tour" : "Tour bundles"}</h2>
          <div className="space-y-3" data-testid="bundle-orders">
            {bundles.map((b) => (
              <div key={b.id} className="border border-border rounded-lg bg-card p-5" data-testid={`bundle-order-${b.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                  <div>
                    <div className="uppercase tracking-[0.2em] text-[10px] text-sage mb-1">{b.items.length} {lang==="it"?"strutture":"structures"}</div>
                    <h4 className="font-display text-xl">{b.name}</h4>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${b.payment_status==="paid"?"bg-sage text-white":"bg-muted text-muted-foreground"}`}>{b.payment_status}</span>
                    <div className="font-display text-2xl text-primary mt-1">{format(b.amount || 0, { digits: 0 })}</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  {b.items.map((it, i) => (
                    <div key={i} className="text-xs bg-sand rounded-md p-2 border border-border">
                      <b>{it.property_name}</b> · {it.rooms} {lang==="it"?"cam":"rooms"} × {it.nights}n
                      <div className="text-muted-foreground">{it.check_in} → {it.check_out}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {isTrade && orders.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <h2 className="font-display text-3xl mb-6">{t("dash.trade_orders_mine")}</h2>
          <div className="border border-border rounded-lg bg-card overflow-x-auto" data-testid="trade-orders">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 uppercase tracking-[0.15em] text-[10px]">
                <tr><th className="text-left p-3">{lang==="it"?"Struttura":"Property"}</th><th className="text-left p-3">Titolo</th><th className="text-left p-3">Camere</th><th className="text-left p-3">Date</th><th className="text-right p-3">Totale</th><th className="text-left p-3">Stato</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-border">
                    <td className="p-3">{o.property_name}</td>
                    <td className="p-3">{o.offer_title}</td>
                    <td className="p-3">{o.rooms} × {o.nights}n</td>
                    <td className="p-3">{o.check_in} → {o.check_out}</td>
                    <td className="p-3 text-right font-display text-lg">{format(o.amount || 0, { digits: 0 })}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${o.payment_status==="paid"?"bg-sage text-white":"bg-muted text-muted-foreground"}`}>{o.payment_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {buying && <BuyDialog offer={buying} onClose={()=>setBuying(null)} format={format}/>}
      {isTrade && <BundlePanel items={bundleItems} offers={offers} setItems={setBundleItems} format={format}/>}
    </div>
  );
}
